import i18n from "@/core/i18n";
import type errors from "@repo/i18n/locales/en/errors.json";
import { LocaleEnum, type StoredErrorFields } from "@repo/schema";
import type { TFunction } from "i18next";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

type ErrorsKey = keyof typeof errors;

/** A failure stored on a row, ready to render: a localized headline, raw details. */
export interface StoredErrorText {
  headline: string;
  /** The raw `errorMessage`, unless it only repeats the headline's English. */
  details: string | null;
}

const describe = (
  t: TFunction<"errors">,
  row: StoredErrorFields,
): StoredErrorText | null => {
  const { errorCode, errorParams, errorMessage } = row;
  // Rows that failed before codes existed: the raw text is all there is.
  if (!errorCode) return errorMessage ? { headline: errorMessage, details: null } : null;

  const options = { ...errorParams, defaultValue: errorMessage ?? errorCode };
  const headline = t(errorCode as ErrorsKey, options);
  // An API-authored failure stores the catalog's own English; only upstream
  // text (an ML exception, an SDK error) says more than the headline.
  const english = i18n.getFixedT(LocaleEnum.EN, "errors")(errorCode as ErrorsKey, options);
  return { headline, details: errorMessage && errorMessage !== english ? errorMessage : null };
};

/**
 * `(row) => { headline, details } | null` for a model, conversion or
 * confidence report that failed (`errorCode` / `errorParams` / `errorMessage`).
 * Null when the row carries no failure. Re-renders with the language.
 */
export const useStoredErrorText = () => {
  const { t } = useTranslation("errors");
  return useCallback((row: StoredErrorFields) => describe(t, row), [t]);
};
